/**
 * AtlasScaleView — Knowledge Atlas primary surface (v5 pattern).
 *
 * Ported from the operator-approved mockup at
 * /app/frontend/public/mockups/ka_scale_optionA.html (v5).
 *
 * Four coexisting states in a single right pane, routed by
 * (docFocus > drillST > viewMode):
 *   • viewMode='connection' — Sub-Topics Connection force graph
 *   • viewMode='list'       — Sub-Topics List sortable table
 *   • drillST=<st>          — Sub-Topic Wiki page (concept list + mini-graph)
 *   • docFocus=<doc>        — Document Impact Report
 *
 * Left rail hosts a searchable Sub-Topic list, a "My uploads" panel
 * (click any doc → Impact Report), and a breadcrumb bar that appears
 * when drilled into a Sub-Topic or Document.
 *
 * Design invariants (operator direction 2026-07-06 → 2026-07-07):
 *   • Iceberg blue #22d3ee for standard, Amber #fbbf24 for NEW.
 *   • White label text on orbs: "X | Y" (X=total, Y=new) with an
 *     SVG line divider (taller top+bottom, 2.2 px thick, round caps).
 *   • Every header/label/tab has a definition tooltip (data-tip).
 *   • Concept tab REMOVED — Concepts are reached only by drilling
 *     into a Sub-Topic wiki.
 *   • Orb grows ~20% on hover; tooltip explains the numbers.
 *   • Concept Quality Score = weighted blend of Mentions × Centrality
 *     × Recency (Option D column). Column header "Quality".
 *
 * Data source: live `atlasEntities` array from the parent explorer.
 * Sub-Topics are aggregated client-side by `entity_type`. Pathways
 * are the union of every entity's `edges[]` (targets in the same
 * tenant). Recent uploads are supplied via props.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as d3 from 'd3';

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

// SPD v5.68 (2026-07-11) — enforce Canadian English on every display label.
// The Knowledge Graph pipeline (M0) tags entities with American spellings
// (e.g. entity_type='organization'). We do NOT rewrite the database here —
// this is a pure display-time transformation. Data-side migration is a
// separate one-shot task pending operator go-ahead.
const AMERICAN_TO_CANADIAN = [
  [/\bOrganization\b/g, 'Organisation'],
  [/\borganization\b/g, 'organisation'],
  [/\bOrganizations\b/g, 'Organisations'],
  [/\borganizations\b/g, 'organisations'],
  [/\bDefense\b/g, 'Defence'],
  [/\bdefense\b/g, 'defence'],
  [/\bLabor\b/g, 'Labour'],
  [/\blabor\b/g, 'labour'],
  [/\bBehavior\b/g, 'Behaviour'],
  [/\bbehavior\b/g, 'behaviour'],
  [/\bColor\b/g, 'Colour'],
  [/\bcolor\b/g, 'colour'],
  [/\bAnalyze\b/g, 'Analyse'],
  [/\banalyze\b/g, 'analyse'],
  [/\bRecognize\b/g, 'Recognise'],
  [/\brecognize\b/g, 'recognise'],
  [/\bPersonalization\b/g, 'Personalisation'],
  [/\bpersonalization\b/g, 'personalisation'],
  [/\bOptimize\b/g, 'Optimise'],
  [/\boptimize\b/g, 'optimise'],
  [/\bOptimization\b/g, 'Optimisation'],
  [/\boptimization\b/g, 'optimisation'],
  [/\bCatalog\b/g, 'Catalogue'],
  [/\bcatalog\b/g, 'catalogue'],
  [/\bArtifact\b/g, 'Artefact'],
  [/\bartifact\b/g, 'artefact'],
  [/\bArtifacts\b/g, 'Artefacts'],
  [/\bartifacts\b/g, 'artefacts'],
];
const canadianise = (s) => {
  if (!s || typeof s !== 'string') return s;
  let out = s;
  for (const [re, rep] of AMERICAN_TO_CANADIAN) out = out.replace(re, rep);
  return out;
};

// SPD v5.68 · operator directive 2026-07-11 — canonical Topic display name.
// The DB partition slug (`searchinc_thesis_2026`) is preserved for routing;
// this map controls what the user sees. Add future entries as needed; unknown
// slugs fall through to a canadianised, space-separated humanisation.
const TOPIC_DISPLAY_MAP = {
  'searchinc_thesis_2026': 'SearchInc_TheRevolution',
};
export const topicDisplayName = (slug) => {
  if (!slug) return 'Topic';
  if (TOPIC_DISPLAY_MAP[slug]) return TOPIC_DISPLAY_MAP[slug];
  return canadianise(slug.replace(/_/g, ' '));
};

const humanEntityType = (t) => canadianise(
  (t || 'unknown').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
);

// SPD v5.66 · D4 Issue 4 — semantic title takes precedence over the internal
// filename. Falls back to filename → doc_id → id so nothing renders as a
// blank when the upload predates the title-gen pipeline.
const displayDocName = (d) => (
  (d && (d.semantic_title || d.filename || d.doc_id || d.id)) || ''
);

// SPD v5.83 · Phase 2 (2026-07-14) — extended "new" definition.
// A concept counts as "new" (for sort priority + top-strip stats + sidebar
// badges) if EITHER its `first_committed_at` OR its `last_strengthened_at`
// exceeds the tenant's `newSince` marker. Previously the frontend only
// checked `first_committed_at`, which missed the very common case of an
// existing concept gaining a new edge from a fresh upload — the M4 pipeline
// stamps `last_strengthened_at` for exactly that scenario (SPD v5.80).
// Without this two-signal check, the whole "Concepts Strengthened" tile
// silently read 0 and existing sub-topics were locked to the bottom of the
// left-sidebar sort even after material downstream change.
const isEntityNewSince = (e, newSinceIso) => {
  if (!e) return false;
  // IJ-340 · when a tenant has no refresh marker yet (brand-new
  // partition), treat every entity as new so the counters show real
  // activity instead of a misleading 0.
  if (!newSinceIso) return true;
  try {
    const boundary = new Date(newSinceIso).getTime();
    const fc = e.first_committed_at ? new Date(e.first_committed_at).getTime() : 0;
    const ls = e.last_strengthened_at ? new Date(e.last_strengthened_at).getTime() : 0;
    return fc > boundary || ls > boundary;
  } catch { return false; }
};

// True iff the entity itself was NET-NEW after the boundary (first commit
// stamp is fresh). Used to separate "brand-new" from "strengthened" in the
// top-strip stats — strengthened = new material - net new.
const isEntityNetNewSince = (e, newSinceIso) => {
  if (!e?.first_committed_at) return false;
  if (!newSinceIso) return true;   // IJ-340 · brand-new tenant, every commit counts
  try {
    return new Date(e.first_committed_at).getTime() > new Date(newSinceIso).getTime();
  } catch { return false; }
};

// True iff the entity was STRENGTHENED after the boundary but was already
// committed BEFORE the boundary. That is, an existing concept that gained
// at least one new edge in the latest M4 pass.
const isEntityStrengthenedSince = (e, newSinceIso) => {
  if (!e?.last_strengthened_at) return false;
  if (!newSinceIso) return false;  // IJ-340 · no prior baseline means no concept can be "strengthened relative to it"
  try {
    const boundary = new Date(newSinceIso).getTime();
    const ls = new Date(e.last_strengthened_at).getTime();
    const fc = e.first_committed_at ? new Date(e.first_committed_at).getTime() : 0;
    return ls > boundary && fc <= boundary;
  } catch { return false; }
};

// -----------------------------------------------------------------------------
// Definition-tooltip infrastructure (data-tip attribute → global tooltip div)
// -----------------------------------------------------------------------------
const useGlobalDefTip = () => {
  useEffect(() => {
    let currentEl = null;
    const el = document.createElement('div');
    el.className = 'asv-def-tip';
    document.body.appendChild(el);
    const onMove = (e) => {
      const tipEl = e.target.closest?.('[data-tip]');
      if (tipEl) {
        if (tipEl !== currentEl) {
          const label = tipEl.getAttribute('data-tip-label') || 'Definition';
          const text  = tipEl.getAttribute('data-tip');
          el.innerHTML = `<div class="asv-dt-label">${label}</div>${text}`;
          currentEl = tipEl;
        }
        const w = window.innerWidth, h = window.innerHeight;
        const est = el.getBoundingClientRect();
        let x = e.clientX + 16, y = e.clientY + 16;
        if (x + est.width  + 20 > w) x = e.clientX - est.width  - 12;
        if (y + est.height + 20 > h) y = e.clientY - est.height - 12;
        el.style.display = 'block';
        el.style.left = x + 'px';
        el.style.top  = y + 'px';
      } else if (currentEl) {
        el.style.display = 'none';
        currentEl = null;
      }
    };
    document.addEventListener('mousemove', onMove);
    return () => {
      document.removeEventListener('mousemove', onMove);
      el.remove();
    };
  }, []);
};

// -----------------------------------------------------------------------------
// Sub-Topic aggregation
// -----------------------------------------------------------------------------
const aggregateSubTopics = (entities, newSinceIso) => {
  const map = new Map();
  entities.forEach((e) => {
    const key = e.entity_type || 'unknown';
    let bucket = map.get(key);
    if (!bucket) {
      bucket = { id: key, name: humanEntityType(key), entities: [], newC: 0, netNewC: 0, strengthenedC: 0, isNew: false, lastUpdated: 0, newDocIds: new Set(), edgeTotal: 0 };
      map.set(key, bucket);
    }
    bucket.entities.push(e);
    bucket.edgeTotal += (e.edges?.length || 0);
    const isNet = isEntityNetNewSince(e, newSinceIso);
    const isStr = isEntityStrengthenedSince(e, newSinceIso);
    if (isNet || isStr) {
      bucket.newC += 1;
      bucket.isNew = true;
      if (isNet) bucket.netNewC += 1;
      if (isStr) bucket.strengthenedC += 1;
      // Track which document(s) triggered the "new" flag on this Sub-Topic.
      (e.source_documents || []).forEach((d) => bucket.newDocIds.add(d));
      const auditDoc = e.audit_record?.payload?.source_doc;
      if (auditDoc) bucket.newDocIds.add(auditDoc);
    }
    if (e.first_committed_at) {
      const t = new Date(e.first_committed_at).getTime();
      if (t > bucket.lastUpdated) bucket.lastUpdated = t;
    }
  });
  return Array.from(map.values()).map((b) => ({
    ...b,
    conceptCount: b.entities.length,
    // D6/D7 Edge Density = average edges per Concept in the Sub-Topic.
    // Higher = more connected / subject-matter-dense Sub-Topic. Used as the
    // secondary sort key on the rail list (primary = conceptCount DESC).
    edgeDensity: b.entities.length > 0 ? (b.edgeTotal / b.entities.length) : 0,
    newDocIds: Array.from(b.newDocIds),
  }));
};

// Resolve a set of doc IDs → an enriched, human-readable label listing the
// contributing document filenames. Used to enrich amber NEW badges with
// the source doc names (Phase 2 D2_KA feedback).
const resolveNewDocNames = (docIds, documents) => {
  if (!docIds || docIds.length === 0) return null;
  const byId = new Map(documents.map((d) => [d.doc_id || d.id, d]));
  const names = docIds
    .map((id) => byId.get(id))
    .filter(Boolean)
    .map((d) => displayDocName(d));
  if (names.length === 0) return null;
  return names;
};

// Cross-Sub-Topic pathway aggregator (returns [{source, target, weight, isNew}])
const aggregateStEdges = (entities, subtopics, entityById, newSinceIso) => {
  const typeById = new Map(entities.map((e) => [e.entity_id, e.entity_type]));
  const edgeMap = new Map();
  entities.forEach((e) => {
    const sa = e.entity_type;
    const sourceNew = isEntityNewSince(e, newSinceIso);
    (e.edges || []).forEach((ed) => {
      const sb = typeById.get(ed.target);
      if (!sa || !sb || sa === sb) return;
      const key = sa < sb ? `${sa}|${sb}` : `${sb}|${sa}`;
      const targetE = entityById.get(ed.target);
      const targetNew = targetE ? isEntityNewSince(targetE, newSinceIso) : false;
      const rec = edgeMap.get(key) || { source: sa < sb ? sa : sb, target: sa < sb ? sb : sa, weight: 0, isNew: false };
      rec.weight += 1;
      if (sourceNew || targetNew) rec.isNew = true;
      edgeMap.set(key, rec);
    });
  });
  const arr = Array.from(edgeMap.values());
  return arr;
};

// -----------------------------------------------------------------------------
// Document → Sub-Topic / Concept impact
// -----------------------------------------------------------------------------
const buildDocImpact = (entities) => {
  const docToConcepts = new Map();
  entities.forEach((e) => {
    const docs = e.source_documents || [];
    docs.forEach((d) => {
      if (!docToConcepts.has(d)) docToConcepts.set(d, new Set());
      docToConcepts.get(d).add(e.entity_id);
    });
    // Fallback: audit_record.payload.source_doc if edge is missing.
    const singleDoc = e.audit_record?.payload?.source_doc;
    if (singleDoc && (!docs || docs.length === 0)) {
      if (!docToConcepts.has(singleDoc)) docToConcepts.set(singleDoc, new Set());
      docToConcepts.get(singleDoc).add(e.entity_id);
    }
  });
  return docToConcepts;
};

// =============================================================================
// Main component
// =============================================================================
export const AtlasScaleView = ({
  entities = [],
  documents = [],
  newSince = null,
  tenant = null,
  sharedSubTopics = [],     // Sub-Topic entity_types present in BOTH the primary and secondary Topic (D9 Slide 4 fix, 2026-02-08). Recolours matching orbs amber on the Sub-Topics Connection canvas.
  compareTenant = null,     // Secondary Topic id, used to gate the shared-set amber overlay.
  onSelectConceptEntity,   // (entityId) => void  – called when a Concept row is clicked in a Wiki page
  mapResetToken = 0,       // SPD v5.73 (2026-02-11) — increments when the top-level Map tab is clicked; forces a return to the Connection sub-view.
}) => {
  useGlobalDefTip();

  // Fast lookup of shared Sub-Topic ids for the amber overlay. Only
  // active while `compareTenant` is set — clearing it drops the overlay.
  const sharedStIdSet = useMemo(() => {
    if (!compareTenant || !Array.isArray(sharedSubTopics)) return null;
    return new Set(sharedSubTopics.map((s) => (typeof s === 'string' ? s : s?.id || s?.entity_type)));
  }, [compareTenant, sharedSubTopics]);

  // ---- Derived data (client-side aggregation, fast enough for 5k entities) --
  const entityById = useMemo(() => new Map(entities.map((e) => [e.entity_id, e])), [entities]);
  const subtopics = useMemo(() => aggregateSubTopics(entities, newSince), [entities, newSince]);
  const stEdges   = useMemo(() => aggregateStEdges(entities, subtopics, entityById, newSince), [entities, subtopics, entityById, newSince]);
  const docToConcepts = useMemo(() => buildDocImpact(entities), [entities]);

  const newConnectionCount = useMemo(() => stEdges.filter((e) => e.isNew).length, [stEdges]);
  const newConceptCountTotal = useMemo(() => entities.reduce((n, e) => (isEntityNewSince(e, newSince) ? n + 1 : n), 0), [entities, newSince]);

  // ---- UI state ---
  const [viewMode, setViewMode] = useState('connection');  // 'connection' | 'list'
  const [drillSt, setDrillSt]   = useState(null);          // Sub-Topic id
  const [docFocus, setDocFocus] = useState(null);          // Document id
  const [query, setQuery]       = useState('');
  // v6 rail collapse: default shows top-10 Sub-Topics; toggle expands the full list.
  const [railExpanded, setRailExpanded] = useState(false);

  // SPD v5.73 (2026-02-11) — Reset to Connection (bubbles) sub-view every
  // time the parent bumps mapResetToken (each Map-tab click). Also clears
  // any drill so a fresh Map click returns the operator to the landing
  // orb graph, not to a drilled-in wiki they'd navigated into earlier.
  // Guarded on `> 0` so the initial mount doesn't spuriously reset.
  useEffect(() => {
    if (mapResetToken > 0) {
      setViewMode('connection');
      setDrillSt(null);
      setDocFocus(null);
    }
  }, [mapResetToken]);

  const currentSt = useMemo(() => (drillSt ? subtopics.find((s) => s.id === drillSt) : null), [drillSt, subtopics]);
  const currentDoc = useMemo(() => (docFocus ? documents.find((d) => d.doc_id === docFocus) : null), [docFocus, documents]);

  const openWiki = (st) => { setDrillSt(st.id); setDocFocus(null); };
  const openImpact = (docId) => { setDocFocus(docId); setDrillSt(null); };
  const clearDrill = () => { setDrillSt(null); setDocFocus(null); };

  // Doc-focus → set of concept ids attributed to this doc.
  const focusConceptIds = useMemo(() => {
    if (!docFocus) return null;
    return docToConcepts.get(docFocus) || new Set();
  }, [docFocus, docToConcepts]);

  // Doc-focus → set of Sub-Topic ids touched.
  const focusStIds = useMemo(() => {
    if (!focusConceptIds) return null;
    const set = new Set();
    entities.forEach((e) => { if (focusConceptIds.has(e.entity_id)) set.add(e.entity_type); });
    return set;
  }, [focusConceptIds, entities]);

  // ---- Filtered Sub-Topic list for the left rail ---
  // D7-2: sort by (# Concepts DESC, then edgeDensity DESC). Top 10 shown by
  // default; the "Full list" toggle at the bottom of the rail expands the rest.
  const filteredSubtopics = useMemo(() => {
    let arr = subtopics;
    if (focusStIds) arr = arr.filter((s) => focusStIds.has(s.id));
    if (query && !drillSt) {
      const q = query.toLowerCase();
      arr = arr.filter((s) => s.name.toLowerCase().includes(q));
    }
    // Sort order — original KA design intent (restored 2026-02-14):
    //   1. Sub-Topics with new Concepts (newC > 0) come first
    //   2. Within the "has-new" group, higher newC ranks higher
    //   3. Then existing sub-topics by conceptCount DESC (mature-corpus tie-break)
    //   4. Final tie-break on edgeDensity DESC
    return arr.slice().sort((a, b) => {
      const aHasNew = (a.newC || 0) > 0 ? 1 : 0;
      const bHasNew = (b.newC || 0) > 0 ? 1 : 0;
      if (aHasNew !== bHasNew) return bHasNew - aHasNew;      // new first
      const dn = (b.newC || 0) - (a.newC || 0);
      if (dn !== 0) return dn;                                 // more-new first
      const dc = (b.conceptCount || 0) - (a.conceptCount || 0);
      if (dc !== 0) return dc;                                 // bigger next
      return (b.edgeDensity || 0) - (a.edgeDensity || 0);
    });
  }, [subtopics, focusStIds, query, drillSt]);

  // Slice-for-render honours railExpanded. Also collapse when a search
  // filter is active and returns ≤ 10 matches (toggle becomes noise).
  const railShownSubtopics = useMemo(() => {
    if (railExpanded) return filteredSubtopics;
    return filteredSubtopics.slice(0, 10);
  }, [filteredSubtopics, railExpanded]);

  return (
    <div className="asv-root" data-testid="atlas-scale-view">
      <ScaleViewStyle />
      {/* SPD v5.74 (2026-02-11) — Toggle labels REVERTED to operator-approved
          baseline `atlas-scale-view-v4` per operator directive GB-001 REVERT
          (2026-02-11). Functional auto-drill logic on List-View click stays
          (approved via IJ-014 "two-view model"), but the LABEL strings and
          tooltip strings return to the prior approved copy. */}
      {!drillSt && !docFocus && (
        <div className="asv-mode-toggle" data-testid="asv-mode-toggle">
          <button
            type="button"
            data-testid="asv-mode-connection"
            data-tip="See how your Sub-Topics connect."
            data-tip-label="Sub-Topics Connection"
            className={viewMode === 'connection' ? 'active' : ''}
            onClick={() => { setViewMode('connection'); clearDrill(); }}
          >Sub-Topics Connection</button>
          <button
            type="button"
            data-testid="asv-mode-list"
            data-tip="Sub-Topics as a sortable table."
            data-tip-label="Sub-Topics List"
            className={viewMode === 'list' ? 'active' : ''}
            onClick={() => {
              setViewMode('list');
              // Auto-select the largest Sub-Topic (already sorted by
              // conceptCount DESC in filteredSubtopics) so List View lands
              // the operator directly on the D12 layout — approved via
              // IJ-014 "two-view model, no drill-down beyond 2 levels".
              if (!drillSt) {
                const first = filteredSubtopics[0];
                if (first) setDrillSt(first.id);
              }
            }}
          >Sub-Topics List</button>
        </div>
      )}

      <div className="asv-canvas">
        <div className="asv-split">

          {/* LEFT panel · Sub-Topic navigation ---------------------------
              D12 (operator SPD v5.68, 2026-07-11):
              - MY UPLOADS block removed from the panel (moved to Documents tab).
              - "← All Sub-Topics" crumb bar removed — panel stays static when
                the user drills into a Sub-Topic; only the selected row
                becomes highlighted (via .active class).
              - Search placeholder stays "Search Sub-Topics…" regardless of
                drill state — no context-switch.
              SPD v5.73 (2026-02-11) — Left rail now always visible in both
              Bubble View and List View. The prior "collapse rail when in
              List with no drill" branch is gone because List View now
              auto-selects the largest Sub-Topic on entry, so the rail is
              never redundant. */}
          <div className="asv-list-col">
            <div className="asv-list-search">
              <input
                data-testid="asv-search-input"
                data-tip="Filter the Sub-Topic list on the left and the graph on the right by typing part of a Sub-Topic name."
                data-tip-label="Search"
                placeholder="Search Sub-Topics…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <div className="asv-list-header" data-testid="asv-list-header">
              <span>SUB-TOPIC</span>
              <span>#&nbsp;OF&nbsp;CONCEPTS</span>
            </div>
            <div className="asv-list-body" data-testid="asv-list-body">
              {railShownSubtopics.map((st) => {
                const amber = st.isNew || (focusStIds && focusStIds.has(st.id));
                const active = drillSt === st.id;
                const newDocNames = resolveNewDocNames(st.newDocIds, documents);
                const tipText = newDocNames && newDocNames.length
                  ? `Added by: ${newDocNames.slice(0, 3).join(', ')}${newDocNames.length > 3 ? ` +${newDocNames.length - 3} more` : ''}. Click to open this Sub-Topic's wiki page.`
                  : "Click to open this Sub-Topic's wiki page — description, newest Concept, all Concepts, and contributing documents.";
                return (
                  <div
                    key={st.id}
                    data-testid={`asv-st-row-${st.id}`}
                    data-tip={tipText}
                    data-tip-label={st.name}
                    className={'asv-st-row' + (amber ? ' amber' : '') + (active ? ' active' : '')}
                    onClick={() => openWiki(st)}
                  >
                    <div className="asv-st-name">{st.name}</div>
                    {st.newC > 0 && (
                      <span
                        className="asv-new-pill"
                        data-testid={`asv-st-new-pill-${st.id}`}
                        data-tip={newDocNames && newDocNames.length
                          ? `${st.newC} new Concept${st.newC === 1 ? '' : 's'} — contributed by: ${newDocNames.join(', ')}`
                          : `${st.newC} new Concept${st.newC === 1 ? '' : 's'} since the last overnight refresh.`}
                        data-tip-label="New Concepts"
                      >+{st.newC} NEW</span>
                    )}
                    <div className="asv-st-num">{st.conceptCount}</div>
                  </div>
                );
              })}
            </div>
            {/* D6-Q3 / D9-5 : "Full list of Sub-Topics" collapsable toggle.
                Hidden only when there is an active search AND the search
                already fits in ≤ 10 rows (toggle would be noise). */}
            {(filteredSubtopics.length > 10 || (!query && filteredSubtopics.length > 0)) && (
              <div
                data-testid="asv-rail-full-list-toggle"
                className={'asv-rail-full-list-toggle' + (railExpanded ? ' expanded' : '')}
                onClick={() => setRailExpanded((v) => !v)}
                data-tip="Show every Sub-Topic in this Topic, not just the top ten by Concept count."
                data-tip-label="Full list"
              >
                <span>{railExpanded ? 'Hide Full list' : `Full list of Sub-Topics (${filteredSubtopics.length})`}</span>
                <span className="caret">{railExpanded ? '▲' : '▼'}</span>
              </div>
            )}
            <div className="asv-list-footer">
              <span>{filteredSubtopics.length} of {subtopics.length} Sub-Topics</span>
              <span>{docFocus ? 'Viewing Impact Report' : drillSt ? 'Click any Sub-Topic to pivot →' : ''}</span>
            </div>
          </div>

          {/* RIGHT pane ------------------------------------------------- */}
          <div className="asv-right-col" data-testid="asv-right-pane">
            {docFocus ? (
              <ImpactReport
                doc={currentDoc}
                entities={entities}
                subtopics={subtopics}
                focusConceptIds={focusConceptIds || new Set()}
                focusStIds={focusStIds || new Set()}
                onOpenSubTopic={openWiki}
                onSelectConcept={onSelectConceptEntity}
              />
            ) : drillSt ? (
              <SubTopicWiki
                st={currentSt}
                tenant={tenant}
                entities={entities}
                documents={documents}
                docToConcepts={docToConcepts}
                newSince={newSince}
                query={query}
                onOpenDoc={openImpact}
                onSelectConcept={onSelectConceptEntity}
                stEdges={stEdges}
                subtopics={subtopics}
                onViewModeChange={(mode) => { setViewMode(mode); clearDrill(); }}
              />
            ) : (
              // SPD v5.73 (2026-02-11) — Two-view model. Only Bubble View
              // (viewMode='connection') or List View (viewMode='list') are
              // valid landing states. List View auto-drills into the largest
              // Sub-Topic via the toggle's onClick handler, so `!drillSt`
              // here always implies Bubble View. The retired
              // SubTopicsListTable component is no longer rendered — it is
              // preserved on disk for potential future roll-back but no
              // longer part of the visible surface.
              <ConnectionGraph
                subtopics={subtopics}
                stEdges={stEdges}
                newConnectionCount={newConnectionCount}
                query={query}
                onOpenSubTopic={openWiki}
                sharedStIdSet={sharedStIdSet}
                compareTenant={compareTenant}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AtlasScaleView;

// =============================================================================
// Sub-Topics Connection graph (D3 force + zoom)
// =============================================================================
const ConnectionGraph = ({ subtopics, stEdges, newConnectionCount, query, onOpenSubTopic, sharedStIdSet = null, compareTenant = null }) => {
  const svgRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!svgRef.current || !wrapRef.current) return;
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();
    const w = wrapRef.current.clientWidth || 1000;
    const h = wrapRef.current.clientHeight || 600;
    svg.attr('viewBox', `0 0 ${w} ${h}`);

    const zoomG = svg.append('g');
    const zoom = d3.zoom().scaleExtent([0.25, 6]).on('zoom', (ev) => zoomG.attr('transform', ev.transform));
    svg.call(zoom);

    const q = (query || '').toLowerCase();
    const queryMatch = (s) => !q || s.name.toLowerCase().includes(q);
    const nodes = subtopics.map((s) => ({ ...s }));
    const links = stEdges.map((e) => ({ ...e }));

    if (nodes.length === 0) return;

    const sim = d3.forceSimulation(nodes)
      .force('link', d3.forceLink(links).id((d) => d.id).distance(180).strength((d) => Math.min(1, (d.weight || 1) / 20)))
      .force('charge', d3.forceManyBody().strength(-550))
      .force('center', d3.forceCenter(w / 2, h / 2))
      .force('x', d3.forceX(w / 2).strength(0.03))
      .force('y', d3.forceY(h / 2).strength(0.03))
      .force('collide', d3.forceCollide((d) => 34 + Math.sqrt(d.conceptCount) * 2.8));

    const linkSel = zoomG.append('g').selectAll('line').data(links).join('line')
      .attr('stroke', (d) => d.isNew ? 'rgba(251,191,36,0.55)' : 'rgba(34,211,238,0.20)')
      .attr('stroke-width', (d) => d.isNew ? 1.0 + Math.log((d.weight || 1) + 1) * 0.4 : 0.5 + Math.log((d.weight || 1) + 1) * 0.4)
      .attr('filter', (d) => d.isNew ? 'drop-shadow(0 0 3px rgba(251,191,36,0.35))' : null);

    const nodeSel = zoomG.append('g').selectAll('g').data(nodes).join('g')
      .style('cursor', 'pointer')
      .attr('opacity', (d) => !queryMatch(d) ? 0.2 : 1);

    const isAmber = (d) => d.isNew || d.newC > 0 || (sharedStIdSet && sharedStIdSet.has(d.id));
    nodeSel.append('circle').attr('class', 'orb-circle')
      .attr('r', (d) => 14 + Math.sqrt(d.conceptCount) * 2.4)
      .attr('fill', (d) => isAmber(d) ? 'rgba(251,191,36,0.20)' : 'rgba(34,211,238,0.14)')
      .attr('stroke', (d) => isAmber(d) ? '#fbbf24' : '#22d3ee')
      .attr('stroke-width', 1.8)
      .attr('filter', (d) => isAmber(d) ? 'drop-shadow(0 0 12px #fbbf24)' : 'drop-shadow(0 0 8px #22d3ee)');

    // Orb centre label — "X | Y" (X=total, Y=new) as a group with SVG line divider.
    const centreG = nodeSel.append('g').attr('class', 'st-count');
    centreG.filter((d) => d.newC === 0).append('text')
      .attr('text-anchor', 'middle').attr('dy', 4).attr('fill', '#ffffff').attr('font-size', 12)
      .text((d) => d.conceptCount);
    const split = centreG.filter((d) => d.newC > 0);
    split.append('text').attr('text-anchor', 'end').attr('x', -5).attr('y', 4)
      .attr('fill', '#ffffff').attr('font-size', 12).text((d) => d.conceptCount);
    split.append('line').attr('x1', 0).attr('x2', 0).attr('y1', -9).attr('y2', 9)
      .attr('stroke', '#ffffff').attr('stroke-width', 2.2).attr('stroke-linecap', 'round');
    split.append('text').attr('text-anchor', 'start').attr('x', 5).attr('y', 4)
      .attr('fill', '#ffffff').attr('font-size', 12).attr('font-weight', 700).text((d) => d.newC);

    nodeSel.append('text').attr('class', 'st-label')
      .attr('fill', 'rgba(224,247,250,0.85)').attr('font-size', 11).attr('text-anchor', 'middle')
      .attr('dy', (d) => 22 + Math.sqrt(d.conceptCount) * 1.4).text((d) => d.name);

    // Hover: definition tooltip + orb grow 20%.
    const hover = d3.select(wrapRef.current).select('.asv-hovercard');
    const GROW = 1.20;
    const applyGrow = (g, d, grow) => {
      const s = grow ? GROW : 1;
      const baseR = 14 + Math.sqrt(d.conceptCount) * 2.4;
      const labelDY = 22 + Math.sqrt(d.conceptCount) * 1.4;
      const sel = d3.select(g);
      sel.select('.orb-circle').transition().duration(180).attr('r', baseR * s).attr('stroke-width', 1.8 * (grow ? 1.15 : 1));
      sel.select('g.st-count').transition().duration(180).attr('transform', grow ? `scale(${GROW})` : 'scale(1)');
      sel.select('.st-label').transition().duration(180).attr('font-size', 11 * s).attr('dy', grow ? labelDY * GROW + 4 : labelDY);
    };
    nodeSel
      .on('mouseenter', function (ev, d) { applyGrow(this, d, true); })
      .on('mousemove', function (ev, d) {
        const r = svgRef.current.getBoundingClientRect();
        const newBit = d.newC > 0 ? ` <span style="color:rgba(224,247,250,0.35)">|</span> <span style="color:var(--asv-amber)">${d.newC}</span>` : '';
        hover.style('display', 'block')
          .style('left', (ev.clientX - r.left + 14) + 'px')
          .style('top',  (ev.clientY - r.top + 12) + 'px')
          .html(`<div class="asv-hc-title">${d.name} &nbsp;<span style="color:var(--asv-cyan-dim);font-weight:400;font-size:11px">${d.conceptCount}${newBit}</span></div>
                 <div class="asv-hc-stats"><strong style="color:#f0fdff">${d.conceptCount}</strong> = total Concepts in this Sub-Topic<br>
                 ${d.newC > 0 ? `<strong style="color:var(--asv-amber)">${d.newC}</strong> = Concepts added since last overnight refresh` : '<span style="color:var(--asv-cyan-dim)">No new Concepts since last refresh</span>'}</div>
                 <div class="asv-hc-cta">Click to open Sub-Topic wiki page →</div>`);
      })
      .on('mouseleave', function (ev, d) { applyGrow(this, d, false); hover.style('display', 'none'); })
      .on('click', (ev, d) => onOpenSubTopic(d));

    sim.on('tick', () => {
      linkSel.attr('x1', (d) => d.source.x).attr('y1', (d) => d.source.y).attr('x2', (d) => d.target.x).attr('y2', (d) => d.target.y);
      nodeSel.attr('transform', (d) => `translate(${d.x},${d.y})`);
    });

    const ro = new ResizeObserver(() => {
      const nw = wrapRef.current?.clientWidth || w;
      const nh = wrapRef.current?.clientHeight || h;
      svg.attr('viewBox', `0 0 ${nw} ${nh}`);
      sim.force('center', d3.forceCenter(nw / 2, nh / 2)).alpha(0.3).restart();
    });
    if (wrapRef.current) ro.observe(wrapRef.current);
    return () => { ro.disconnect(); sim.stop(); };
  }, [subtopics, stEdges, query, onOpenSubTopic, sharedStIdSet, compareTenant]);

  return (
    <>
      <div className="asv-graph-header">
        <div className="asv-g-title">
          <span
            data-tip="A Sub-Topic is a cluster of related Concepts extracted from your uploaded documents. A Sub-Topic rolls up to a Topic or Topics."
            data-tip-label="Sub-Topics"
            style={{ color: 'var(--asv-cyan)' }}
          >Sub-Topics ({subtopics.length})</span>
          &nbsp;|&nbsp;
          <span
            data-tip="A link between two Sub-Topics that share Concepts."
            data-tip-label="Sub-Topic Connections"
            style={{ color: 'var(--asv-cyan)' }}
          >Sub-Topic Connections ({stEdges.length})</span>
          &nbsp;|&nbsp;
          <span
            data-tip="Links added since the last overnight refresh."
            data-tip-label="New Connections"
            style={{ color: 'var(--asv-amber)' }}
          >New Connections ({newConnectionCount})</span>&nbsp;since last refresh
        </div>
      </div>
      <div className="asv-graph-stage" ref={wrapRef} data-testid="asv-graph-stage">
        <svg ref={svgRef} className="asv-graph-svg" />
        <div className="asv-hovercard" />
        <div className="asv-graph-legend">
          <span data-tip="A Sub-Topic groups related Concepts extracted from your corpus. Represented as a super-orb on the graph." data-tip-label="Sub-Topic"><span className="dot" style={{ background: 'var(--asv-cyan)', boxShadow: '0 0 8px var(--asv-cyan)' }} />Sub-Topic</span>
          <span data-tip="Sub-Topic that gained at least one new Concept since the last overnight refresh." data-tip-label="Sub-Topic with new Concepts"><span className="dot" style={{ background: 'var(--asv-amber)', boxShadow: '0 0 8px var(--asv-amber)' }} />Sub-Topic with new Concepts</span>
          <span data-tip="Cross-Sub-Topic Pathway created or strengthened by a recent document upload." data-tip-label="New Pathway"><span style={{ display: 'inline-block', width: 16, height: 2, background: 'rgba(251,191,36,0.7)', verticalAlign: 'middle', marginRight: 6 }} />New Pathway since last refresh</span>
        </div>
      </div>
    </>
  );
};

// =============================================================================
// Sub-Topics List table view
// =============================================================================
const SubTopicsListTable = ({ subtopics, totalSubtopics, onOpenSubTopic, documents, sharedStIdSet = null, compareTenant = null }) => (
  <>
    <div className="asv-graph-header">
      <div className="asv-g-title">
        <strong data-tip="Sortable table view of every Sub-Topic. Same data as the Connection graph, but as rows." data-tip-label="Sub-Topics List">Sub-Topics List</strong>
      </div>
    </div>
    <div className="asv-listview">
      <h3>All Sub-Topics · <span style={{ color: 'var(--asv-cyan-dim)', fontWeight: 400, fontSize: 12 }}>{subtopics.length} of {totalSubtopics}</span></h3>
      <p className="asv-lv-sub">Sorted by Concept count · amber row = new material since last refresh</p>
      <table className="asv-lv-table" data-testid="asv-lv-table">
        <thead>
          <tr>
            <th data-tip="A Sub-Topic groups related Concepts extracted from your corpus." data-tip-label="Sub-Topic">Sub-Topic</th>
            <th className="num" data-tip="Total distinct Concepts in this Sub-Topic." data-tip-label="Concepts">Concepts</th>
            <th className="num" data-tip="Concepts added to this Sub-Topic since the last overnight refresh." data-tip-label="New">New</th>
            <th className="num" data-tip="Time since the most recent update to this Sub-Topic (any new Concept or Pathway)." data-tip-label="Last updated">Last updated</th>
            <th style={{ textAlign: 'right' }} data-tip="Open this Sub-Topic's wiki page." data-tip-label="Wiki">Wiki</th>
          </tr>
        </thead>
        <tbody>
          {subtopics.map((st) => {
            const isAmber = st.isNew || st.newC > 0 || (sharedStIdSet && sharedStIdSet.has(st.id));
            const hoursAgo = st.lastUpdated ? Math.round((Date.now() - st.lastUpdated) / 3600000) : null;
            const ago = hoursAgo == null ? '—' : hoursAgo < 1 ? 'just now' : `${hoursAgo}h ago`;
            const newDocNames = resolveNewDocNames(st.newDocIds, documents || []);
            const pillTip = newDocNames && newDocNames.length
              ? `Added by: ${newDocNames.slice(0, 4).join(', ')}${newDocNames.length > 4 ? ` +${newDocNames.length - 4} more` : ''}`
              : 'This Sub-Topic gained one or more new Concepts since the last overnight refresh.';
            return (
              <tr
                key={st.id}
                data-testid={`asv-lv-row-${st.id}`}
                className={isAmber ? 'amber' : ''}
                onClick={() => onOpenSubTopic(st)}
              >
                <td className="name">{st.name}{isAmber && <span className="asv-amber-pill" data-tip={pillTip} data-tip-label={newDocNames && newDocNames.length ? `New — ${newDocNames.length} contributing doc${newDocNames.length === 1 ? '' : 's'}` : 'New'}>new</span>}</td>
                <td className="num">{st.conceptCount}</td>
                <td className="num" style={{ color: st.newC ? 'var(--asv-amber)' : 'var(--asv-cyan-dim)' }}>{st.newC || '—'}</td>
                <td className="num">{ago}</td>
                <td className="drill">Open ›</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </>
);

// =============================================================================
// Sub-Topic Wiki page (drill-in) — v6 layout ported 2026-07-11
// -----------------------------------------------------------------------------
// Source of truth: /app/frontend/public/mockups/subtopic_wiki_v6.html (D12
// operator-approved layout). Every visible string in this component either
// (a) matches the v6 mockup verbatim or (b) reuses a phrase already approved
// in COPY_LEDGER (atlas-scale-view-v2, atlas-onboarding-modal-v3). Copy Judge
// baseline `atlas-subtopic-wiki-v1` is recorded post-ship.
// =============================================================================

// Render a synopsis string that contains **bold** section headers into React
// nodes. Local to this component because the M3 pipeline emits five markers:
//   **Definition.**  **Corpus context.**  **Thematic sections.**
//   **Sources.**  and paragraph-level bold headers within Thematic sections.
// A plain-text render leaks the literal asterisks; this helper is intentionally
// narrow — no full-markdown parser, only **…**.
const renderSynopsisMarkdown = (text) => {
  if (!text) return null;
  const parts = String(text).split(/(\*\*[^*\n]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return (
        <strong key={i} style={{ color: '#f0fdff', fontWeight: 600 }}>
          {part.slice(2, -2)}
        </strong>
      );
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
};

const daysAgoLabel = (iso) => {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '—';
  const diff = Math.floor((Date.now() - t) / 86400000);
  if (diff <= 0) return 'today';
  if (diff === 1) return '1 day ago';
  return `${diff} days ago`;
};

const SubTopicWiki = ({ st, tenant, entities, documents, docToConcepts, newSince, query, onOpenDoc, onSelectConcept, onViewModeChange, stEdges, subtopics }) => {
  // Local UI state — table filter, sort, row expansion, top-10 collapse
  const [filterText, setFilterText]     = useState('');
  const [newOnly, setNewOnly]           = useState(false);
  const [sortKey, setSortKey]           = useState('conn');   // conn | qual | src | cmt | name
  const [sortDir, setSortDir]           = useState('desc');
  const [expandedRows, setExpandedRows] = useState(() => new Set());
  const [tableExpanded, setTableExpanded] = useState(false);

  // Fast entity lookup for edge-target name resolution inside expanded rows.
  const entityById = useMemo(() => {
    const m = new Map();
    (entities || []).forEach((e) => m.set(e.entity_id, e));
    return m;
  }, [entities]);

  // Concepts inside this Sub-Topic
  const conceptsInSt = useMemo(
    () => (entities || []).filter((e) => e.entity_type === st?.id),
    [entities, st]
  );

  // v6 stat strip counts — IJ-341 (2026-08-18) · every tile is now
  // tenant scoped for row consistency with Source Docs which sits
  // alongside them. Sub-topic granularity is preserved via the left-rail
  // `+N NEW` badges on each sub-topic pill.
  const stats = useMemo(() => {
    const conceptIdSet = new Set((entities || []).map((c) => c.entity_id));
    const newIdSet = new Set(
      (entities || []).filter((c) => isEntityNewSince(c, newSince)).map((c) => c.entity_id)
    );

    // New Documents (Topic scoped): distinct docs uploaded since refresh
    // that contributed at least one Concept anywhere in this Topic.
    // IJ-331 · fix field-name mismatch (upload_date fallback).
    // IJ-332 · prefer server-supplied linked_entity_ids.
    // IJ-340 · null newSince means every upload counts.
    // IJ-341 · tenant scoped (was sub-topic scoped).
    let newDocs = 0;
    (documents || []).forEach((d) => {
      const ts = d.upload_date || d.uploaded_at || d.created_at;
      if (!ts) return;
      if (newSince && new Date(ts) <= new Date(newSince)) return;
      const linkedIds = Array.isArray(d.linked_entity_ids) ? d.linked_entity_ids : null;
      const conceptIds = linkedIds && linkedIds.length > 0
        ? linkedIds
        : docToConcepts?.get(d.doc_id);
      if (!conceptIds) { newDocs += 1; return; }  // doc uploaded but no linked concepts yet — still a new doc for the tenant
      newDocs += 1;
    });

    // New Concepts (Topic scoped)
    const newConcepts = newIdSet.size;

    // New Connections (Topic scoped): distinct undirected pairs among
    // every entity in the tenant where at least one endpoint is new
    // since refresh.
    const pairSet = new Set();
    (entities || []).forEach((c) => {
      (c.edges || []).forEach((ed) => {
        const other = ed.target;
        const isPairNew = newIdSet.has(c.entity_id) || newIdSet.has(other);
        if (!isPairNew) return;
        const a = c.entity_id, b = other;
        pairSet.add(a < b ? `${a}|${b}` : `${b}|${a}`);
      });
    });
    const newConns = pairSet.size;

    // Concepts Strengthened (Topic scoped) — every entity in the tenant
    // with a fresh last_strengthened_at stamp and a first_committed_at
    // predating the refresh boundary.
    let strengthened = 0;
    (entities || []).forEach((c) => {
      if (isEntityStrengthenedSince(c, newSince)) strengthened += 1;
    });

    // Source Docs (Topic scoped): total documents in this tenant/Topic.
    const srcDocs = (documents || []).length;

    return { newDocs, newConcepts, newConns, strengthened, srcDocs };
  }, [entities, documents, docToConcepts, newSince]);

  const humanTopic = topicDisplayName(tenant);

  // Quality-lite placeholder score.
  // SPD v5.68 — replaced the previous /100 cap-at-1.00 formula (which gave
  // every top-tier Concept an identical 1.00) with a log10 normaliser that
  // preserves ordering while producing a real spread across the ~0.20-0.95
  // range. Server-side formula remains a P1 backlog item.
  //   raw = 10 × edges + 5 × sources (+12 if new)
  //   score = clamp(0.10, 0.20 + 0.20 · log10(raw), 0.99)
  const qualityOf = (c) => {
    let raw = (c.edges?.length || 0) * 10 + (c.source_documents?.length || 0) * 5;
    if (isEntityNewSince(c, newSince)) raw += 12;
    raw = Math.max(1, raw);
    const score = 0.20 + 0.20 * Math.log10(raw);
    return Math.max(0.10, Math.min(0.99, score));
  };

  // Rows for the table — filtered + sorted
  const tableRows = useMemo(() => {
    let rows = conceptsInSt.slice();
    if (filterText) {
      const q = filterText.toLowerCase();
      rows = rows.filter((c) => (c.entity_name || c.entity_id || '').toLowerCase().includes(q));
    }
    if (newOnly) rows = rows.filter((c) => isEntityNewSince(c, newSince));
    // Apply outer search-in-header query too (rail-level search shared with wiki).
    const outerQ = (query || '').toLowerCase();
    if (outerQ) rows = rows.filter((c) => (c.entity_name || c.entity_id || '').toLowerCase().includes(outerQ));
    const dir = sortDir === 'asc' ? 1 : -1;
    const getK = (r) => {
      switch (sortKey) {
        case 'name': return (r.entity_name || r.entity_id || '').toLowerCase();
        case 'conn': return r.edges?.length || 0;
        case 'qual': return qualityOf(r);
        case 'src':  return r.source_documents?.length || 0;
        case 'cmt':  return r.first_committed_at ? new Date(r.first_committed_at).getTime() : 0;
        default:     return 0;
      }
    };
    rows.sort((a, b) => {
      const va = getK(a), vb = getK(b);
      if (typeof va === 'string') return va.localeCompare(vb) * dir;
      return (va - vb) * dir;
    });
    return rows;
  }, [conceptsInSt, filterText, newOnly, sortKey, sortDir, query, newSince]);

  const totalCount = tableRows.length;
  const shownRows = tableExpanded ? tableRows : tableRows.slice(0, 10);
  const hiddenCount = totalCount - shownRows.length;

  const toggleRow = (rowId) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId); else next.add(rowId);
      return next;
    });
  };

  const clickSort = (k) => {
    if (sortKey === k) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortKey(k); setSortDir((k === 'name' || k === 'cmt') ? 'asc' : 'desc'); }
  };

  const sortCaret = (k) => (sortKey === k ? (sortDir === 'asc' ? '▲' : '▼') : '▲▼');

  // Early-return AFTER all hooks (rules-of-hooks compliance).
  if (!st) return <div className="asv-listview"><p>No Sub-Topic selected.</p></div>;

  return (
    <div className="stw-root" data-testid="atlas-subtopic-wiki">
      {/* D7-1 breadcrumb + D9-4/D10 two-stack view toggle */}
      <div className="stw-breadcrumb-row" data-testid="stw-breadcrumb-row">
        <div className="stw-crumb">
          <span className="prefix">Topic: {humanTopic}</span>
          <span className="sep">&gt;</span>
          <span className="prefix">Sub-Topic:</span>
          <span className="subtopic" data-testid="stw-subtopic-name">{st.name}</span>
          {(st.newC || 0) > 0 && (
            <span className="stw-new-inline" data-testid="stw-new-badge">+{st.newC} NEW</span>
          )}
        </div>
        <div className="stw-view-mode-stack" data-testid="stw-view-mode-stack">
          <div
            className="vm-row"
            data-testid="stw-view-mode-connections"
            data-tip="Return to the Sub-Topics Connection map."
            data-tip-label="Sub-Topic/Connections View"
            onClick={() => onViewModeChange?.('connection')}
          >Sub-Topic/Connections View</div>
          <div
            className="vm-row"
            data-testid="stw-view-mode-concepts"
            data-tip="Return to the Sub-Topics sortable list."
            data-tip-label="Sub-Topic & Concept View"
            onClick={() => onViewModeChange?.('list')}
          >Sub-Topic &amp; Concept View</div>
        </div>
      </div>

      {/* D6-10 stat strip: 2 parallel iceberg lines, 5 cells */}
      <div className="stw-stat-strip" data-testid="stw-stat-strip">
        <div className="stw-stat-cell amber" data-testid="stw-stat-new-docs" data-tip="New documents uploaded to this Topic since the last overnight refresh." data-tip-label="New Documents">
          <div className="sc-lbl">
            New Documents
            {stats.newDocs === 0 && (stats.newConcepts > 0 || stats.newConns > 0) && (
              <span
                className="sc-info-icon"
                data-testid="stw-stat-new-docs-explain"
                data-tip="Yes — this is by design. New Concepts and Connections can surface without new uploads. The overnight refresh re-analyses existing documents with the latest Search.Inc prompts, and cross-Sub-Topic linking can promote Concepts already extracted for another Sub-Topic into this one. Upload a new document to add fresh material; otherwise these deltas reflect what the graph learned from artefacts you have already contributed."
                data-tip-label="Why zero new documents but new Concepts?"
                aria-label="Why zero new documents but new Concepts?"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 14,
                  height: 14,
                  marginLeft: 6,
                  borderRadius: '50%',
                  border: '1px solid rgba(0, 200, 255, 0.55)',
                  color: 'rgba(125, 211, 252, 0.95)',
                  fontSize: 10,
                  fontWeight: 700,
                  lineHeight: 1,
                  cursor: 'help',
                  verticalAlign: 'middle',
                }}
              >?</span>
            )}
          </div>
          <div className="sc-val">{stats.newDocs}</div>
          <div className="sc-sub">since last refresh</div>
        </div>
        <div className="stw-stat-cell amber" data-testid="stw-stat-new-concepts" data-tip="New Concepts extracted for this Topic since the last overnight refresh." data-tip-label="New Concepts">
          <div className="sc-lbl">New Concepts</div>
          <div className="sc-val">{stats.newConcepts}</div>
          <div className="sc-sub">since last refresh</div>
        </div>
        <div className="stw-stat-cell amber" data-testid="stw-stat-new-conns" data-tip="Distinct Connections in this Topic that involve at least one new Concept since the last refresh." data-tip-label="New Connections">
          <div className="sc-lbl">New Connections</div>
          <div className="sc-val">{stats.newConns}</div>
          <div className="sc-sub">since last refresh</div>
        </div>
        <div className="stw-stat-cell amber" data-testid="stw-stat-strengthened" data-tip="Existing Concepts in this Topic that gained a Connection to a new Concept since the last refresh." data-tip-label="Concepts Strengthened">
          <div className="sc-lbl">Concepts Strengthened</div>
          <div className="sc-val">{stats.strengthened}</div>
          <div className="sc-sub">since last refresh</div>
        </div>
        <div className="stw-stat-cell" data-testid="stw-stat-src-docs" data-tip="Total documents contributing to this Topic across all Sub-Topics." data-tip-label="Source Docs">
          <div className="sc-lbl">Source Docs</div>
          <div className="sc-val">{stats.srcDocs}</div>
          <div className="sc-sub">for this Topic</div>
        </div>
      </div>

      {/* Table section — D7-4 no heading; toolbar only */}
      <div className="stw-table-section">
        <div className="stw-table-toolbar">
          <div className="tt-actions">
            <input
              className="tt-filter"
              data-testid="stw-concept-filter"
              placeholder="Filter concepts…"
              value={filterText}
              onChange={(e) => { setFilterText(e.target.value); setTableExpanded(false); }}
            />
            <button
              type="button"
              className={'tt-toggle' + (newOnly ? ' on' : '')}
              data-testid="stw-new-only-toggle"
              data-tip="Show only Concepts added since the last overnight refresh."
              data-tip-label="New only"
              onClick={() => { setNewOnly(!newOnly); setTableExpanded(false); }}
            >New only</button>
          </div>
        </div>

        <table className="stw-ct" data-testid="stw-concept-table">
          <thead>
            <tr>
              <th data-sort="name" onClick={() => clickSort('name')} className={sortKey === 'name' ? 'sort-active' : ''} style={{ width: '38%' }}>Concept <span className="sort-caret">{sortCaret('name')}</span></th>
              <th data-sort="conn" onClick={() => clickSort('conn')} className={'num' + (sortKey === 'conn' ? ' sort-active' : '')}>Connections <span className="sort-caret">{sortCaret('conn')}</span></th>
              <th data-sort="qual" onClick={() => clickSort('qual')} className={'num' + (sortKey === 'qual' ? ' sort-active' : '')}>Quality <span className="sort-caret">{sortCaret('qual')}</span></th>
              <th data-sort="src"  onClick={() => clickSort('src')}  className={'num' + (sortKey === 'src'  ? ' sort-active' : '')}>Sources <span className="sort-caret">{sortCaret('src')}</span></th>
              <th data-sort="cmt"  onClick={() => clickSort('cmt')}  className={sortKey === 'cmt' ? 'sort-active' : ''}>Committed <span className="sort-caret">{sortCaret('cmt')}</span></th>
            </tr>
          </thead>
          <tbody>
            {shownRows.length === 0 && (
              <tr><td colSpan={5} className="stw-empty">No Concepts match the current filter.</td></tr>
            )}
            {shownRows.map((c) => {
              const rowId = c.entity_id;
              const isExp = expandedRows.has(rowId);
              const isNew = isEntityNewSince(c, newSince);
              const q = qualityOf(c);
              const defText = c.claims?.[0]?.text || (c.summary || '').split('\n')[0].replace(/\*\*/g, '');
              const rowClass = 'crow' + (isNew ? ' amber' : '') + (isExp ? ' expanded' : '');
              const edgeList = (c.edges || []).slice().sort((a, b) => (b.weight || 0) - (a.weight || 0)).slice(0, 8);
              const srcNames = c.source_document_names || {};
              return (
                <React.Fragment key={rowId}>
                  <tr
                    className={rowClass}
                    data-testid={`stw-concept-row-${rowId}`}
                    onClick={() => toggleRow(rowId)}
                  >
                    <td>
                      <span className="caret">▶</span>
                      <span className="cn">{c.entity_name || c.entity_id}</span>
                      {isNew && <span className="new-tag">New</span>}
                      <div className="cn-def">{defText}</div>
                    </td>
                    <td className="num">{c.edges?.length || 0}</td>
                    <td className="num">
                      <span className="qbar">
                        <span className="qb-track"><span className="qb-fill" style={{ width: `${Math.round(q * 100)}%` }} /></span>
                        <span className="qb-num">{q.toFixed(2)}</span>
                      </span>
                    </td>
                    <td className="num">{c.source_documents?.length || 0}</td>
                    <td>{daysAgoLabel(c.first_committed_at)}</td>
                  </tr>
                  {isExp && (
                    <tr className="stw-detail-row" data-testid={`stw-concept-detail-${rowId}`}>
                      <td colSpan={5}>
                        <div className="detail-grid">
                          <div>
                            <p className="detail-h">Definition &amp; corpus context</p>
                            <div className="detail-summary">{renderSynopsisMarkdown(c.summary)}</div>
                            {(c.claims && c.claims.length > 0) && (
                              <>
                                <p className="detail-h" style={{ marginTop: 16 }}>Key claims</p>
                                <ul className="detail-claims">
                                  {c.claims.slice(0, 6).map((cl, i) => (
                                    <li key={cl.claim_id || i}>{cl.text}</li>
                                  ))}
                                </ul>
                              </>
                            )}
                            <button
                              type="button"
                              className="detail-open-cta"
                              data-testid={`stw-open-concept-${rowId}`}
                              onClick={(e) => { e.stopPropagation(); onSelectConcept?.(c.entity_id); }}
                            >Open full Concept page →</button>
                          </div>
                          <div>
                            <p className="detail-h">Top connections</p>
                            {edgeList.length === 0 ? (
                              <div className="detail-empty">No Connections yet.</div>
                            ) : (
                              <ul className="detail-conns">
                                {edgeList.map((ed, i) => {
                                  const tgt = entityById.get(ed.target);
                                  const tgtName = tgt?.entity_name || tgt?.entity_id || ed.target;
                                  const tgtType = tgt?.entity_type ? humanEntityType(tgt.entity_type) : '';
                                  return (
                                    <li
                                      key={i}
                                      data-testid={`stw-edge-${rowId}-${ed.target}`}
                                      onClick={(e) => { e.stopPropagation(); onSelectConcept?.(ed.target); }}
                                    >
                                      <span className="cn-tgt">
                                        <span>{tgtName}</span>
                                        {tgtType && <span className="cn-type">{tgtType}</span>}
                                      </span>
                                      <span className="cn-w">{Number(ed.weight ?? 0).toFixed(2)}</span>
                                    </li>
                                  );
                                })}
                              </ul>
                            )}
                            {(c.source_documents && c.source_documents.length > 0) && (
                              <>
                                <p className="detail-h" style={{ marginTop: 16 }}>Source documents</p>
                                <div className="detail-sources">
                                  {c.source_documents.slice(0, 12).map((sd) => {
                                    const fname = srcNames[sd] || sd;
                                    return (
                                      <span
                                        key={sd}
                                        className="src-pill"
                                        data-testid={`stw-source-pill-${rowId}-${sd}`}
                                        onClick={(e) => { e.stopPropagation(); onOpenDoc?.(sd); }}
                                        title={fname !== sd ? sd : undefined}
                                      >
                                        {fname} <span className="dl">↓</span>
                                      </span>
                                    );
                                  })}
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>

        {/* D7-5 collapsable "Show all N Concepts" toggle */}
        {(hiddenCount > 0 || tableExpanded) && totalCount > 10 && (
          <div
            className="stw-table-show-all"
            data-testid="stw-show-all-toggle"
            onClick={() => setTableExpanded((v) => !v)}
          >
            <span>{tableExpanded ? 'Show fewer — collapse to top 10' : `Show all ${totalCount} Concepts`}</span>
            <span className="tsa-caret">{tableExpanded ? '▲' : '▼'}</span>
          </div>
        )}
      </div>
    </div>
  );
};

// =============================================================================
// Document Impact Report
// =============================================================================
const ImpactReport = ({ doc, entities, subtopics, focusConceptIds, focusStIds, onOpenSubTopic, onSelectConcept }) => {
  if (!doc) return <div className="asv-listview"><p>Document not found.</p></div>;
  const impactedConcepts = entities.filter((e) => focusConceptIds.has(e.entity_id))
    .sort((a, b) => (b.edges?.length || 0) - (a.edges?.length || 0));
  const impactedSts = subtopics.filter((s) => focusStIds.has(s.id));
  const primarySt = impactedSts.slice().sort((a, b) => {
    const na = a.entities.filter((e) => focusConceptIds.has(e.entity_id)).length;
    const nb = b.entities.filter((e) => focusConceptIds.has(e.entity_id)).length;
    return nb - na;
  })[0];

  return (
    <>
      <div className="asv-graph-header">
        <div className="asv-g-title"><strong>Document Impact Report</strong> · {displayDocName(doc)}</div>
      </div>
      <div className="asv-impact">
        <div className="asv-impact-hero" data-tip="Summary of every Concept, Pathway and Sub-Topic added or strengthened by this document." data-tip-label="Document Impact Report">
          <div className="h-badge" data-tip="Time since this document was uploaded and processed through the extraction pipeline." data-tip-label="Uploaded">◐ Document impact{doc.age_label ? ` · ${doc.age_label}` : ''}</div>
          <h2>{displayDocName(doc)}</h2>
          <div className="h-meta">
            {doc.uploaded_by && <span>Uploaded by <strong style={{ color: '#fef3c7' }}>{doc.uploaded_by}</strong></span>}
            {doc.uploaded_by && <span className="dot-sep">·</span>}
            <span>Ingested through M0 · M1 · M3 · M4 pipeline</span>
          </div>
        </div>
        <div className="asv-impact-tiles">
          <div className="asv-tile" data-tip="Number of new Concepts extracted from this document via the M0 entity pipeline." data-tip-label="Concepts added">
            <div className="lbl">Concepts added</div>
            <div className="val">+{impactedConcepts.length}</div>
            <div className="sub">across {impactedSts.length} Sub-Topic{impactedSts.length === 1 ? '' : 's'}</div>
          </div>
          <div className="asv-tile" data-tip="Number of Sub-Topics that gained at least one Concept from this document." data-tip-label="Sub-Topics touched">
            <div className="lbl">Sub-Topics touched</div>
            <div className="val">{impactedSts.length}</div>
            <div className="sub">{impactedSts.slice(0, 2).map((s) => s.name).join(', ')}{impactedSts.length > 2 ? ` + ${impactedSts.length - 2} more` : ''}</div>
          </div>
          <div className="asv-tile" data-tip="Total Pathways touching Concepts introduced by this document." data-tip-label="Pathways">
            <div className="lbl">Pathways</div>
            <div className="val">{impactedConcepts.reduce((n, c) => n + (c.edges?.length || 0), 0)}</div>
            <div className="sub">connections created or strengthened</div>
          </div>
          <div className="asv-tile" data-tip="Cross-Sub-Topic Pathways created or strengthened by this document." data-tip-label="Cross-STs">
            <div className="lbl">Cross-STs</div>
            <div className="val">{Math.max(0, impactedSts.length - 1)}</div>
            <div className="sub">between the affected Sub-Topics</div>
          </div>
        </div>

        <div className="asv-wiki-h" data-tip="Sub-Topics that this document contributed at least one Concept to. Click any Sub-Topic to open its wiki page." data-tip-label="Sub-Topics touched">Sub-Topics touched by this document</div>
        <div className="asv-doc-list">
          {impactedSts.map((s) => {
            const localCount = s.entities.filter((e) => focusConceptIds.has(e.entity_id)).length;
            return (
              <div key={s.id} className="asv-doc-row amber" onClick={() => onOpenSubTopic(s)}>
                <div className="d-name">{s.name}</div>
                <div className="d-meta">+{localCount} Concept{localCount === 1 ? '' : 's'} · click to open Sub-Topic wiki →</div>
              </div>
            );
          })}
        </div>

        <div className="asv-wiki-h" data-tip="Every Concept extracted from this document via the M0 entity pipeline." data-tip-label="Concepts added">Concepts added by this document <span style={{ textTransform: 'none', letterSpacing: 0, color: 'var(--asv-cyan-dim)', marginLeft: 'auto' }}>{impactedConcepts.length}</span></div>
        <table className="asv-wiki-table">
          <thead>
            <tr>
              <th data-tip="Concept name as extracted from this document." data-tip-label="Concept">Concept</th>
              <th data-tip="The Sub-Topic this Concept belongs to." data-tip-label="Sub-Topic">Sub-Topic</th>
              <th className="num" data-tip="Concept Quality Score — weighted blend of Mentions × Centrality × Recency." data-tip-label="Quality">Quality</th>
              <th className="num" data-tip="Number of Pathways from this Concept to other Concepts." data-tip-label="Pathways">Pathways</th>
            </tr>
          </thead>
          <tbody>
            {impactedConcepts.slice(0, 200).map((c) => (
              <tr
                key={c.entity_id}
                className="amber"
                data-testid={`asv-impact-concept-row-${c.entity_id}`}
                onClick={() => { if (onSelectConcept) onSelectConcept(c.entity_id); }}
              >
                <td className="name">{c.entity_name || c.entity_id}<span className="asv-new-tag">added</span></td>
                <td>{humanEntityType(c.entity_type)}</td>
                <td className="num">{Math.max(1, Math.min(100, (c.edges?.length || 0) * 10 + (c.source_documents?.length || 0) * 5))}</td>
                <td className="num">{c.edges?.length || 0}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {primarySt && (
          <div className="asv-impact-ctas">
            <button
              type="button"
              className="primary"
              data-tip="Open the wiki page for the Sub-Topic that gained the most Concepts from this document."
              data-tip-label="Primary Sub-Topic"
              onClick={() => onOpenSubTopic(primarySt)}
            >Open the primary Sub-Topic wiki →</button>
          </div>
        )}
      </div>
    </>
  );
};

// =============================================================================
// Inline stylesheet (co-located so the component drops in cleanly)
// =============================================================================
const ScaleViewStyle = () => (
  <style>{`
    .asv-root {
      --asv-bg:        #050b18;
      --asv-panel:     rgba(10, 20, 40, 0.55);
      --asv-border:    rgba(34, 211, 238, 0.22);
      --asv-border-2:  rgba(34, 211, 238, 0.10);
      --asv-cyan:      #22d3ee;
      --asv-cyan-dim:  rgba(34, 211, 238, 0.60);
      --asv-cyan-txt:  #a5f3fc;
      --asv-amber:     #fbbf24;
      --asv-amber-dim: rgba(251, 191, 36, 0.55);
      position: relative;
      padding: 0 32px 12px;
    }
    .asv-mode-toggle {
      position: absolute; top: 8px; right: 32px; z-index: 5;
      display: flex; flex-direction: column;
      background: rgba(6,14,30,0.85); backdrop-filter: blur(6px);
      border: 1px solid var(--asv-border); border-radius: 12px; overflow: hidden;
      min-width: 210px; box-shadow: 0 10px 30px rgba(0,0,0,0.4);
    }
    .asv-mode-toggle button {
      background: transparent; border: 0; border-bottom: 1px solid var(--asv-border-2);
      color: var(--asv-cyan-txt); padding: 10px 14px; text-align: left; cursor: pointer;
      font-size: 11px; letter-spacing: 2px; text-transform: uppercase;
    }
    .asv-mode-toggle button:last-child { border-bottom: 0; }
    .asv-mode-toggle button.active { background: rgba(34,211,238,0.18); color: #f0fdff; }
    .asv-mode-toggle button:hover:not(.active) { background: rgba(34,211,238,0.06); }
    .asv-canvas {
      position: relative;
      border: 0;
      border-radius: 10px;
      background: linear-gradient(180deg, rgba(5,11,24,0.65), rgba(2,6,15,0.90));
      overflow: hidden;
      height: calc(100vh - 220px);
      min-height: 620px;
    }
    .asv-split { display: grid; grid-template-columns: 380px 1fr; gap: 14px; height: 100%; padding: 12px; }
    .asv-split.asv-split-single { grid-template-columns: 1fr; }
    .asv-list-col { display: flex; flex-direction: column; overflow: hidden; background: var(--asv-panel); border: 1px solid var(--asv-border-2); border-radius: 10px; }
    .asv-list-search { padding: 12px 14px; border-bottom: 1px solid var(--asv-border-2); }
    .asv-list-search input { width: 100%; background: rgba(0,0,0,0.35); border: 1px solid var(--asv-border-2); color: var(--asv-cyan-txt); padding: 8px 10px; border-radius: 8px; font-size: 12px; outline: none; }
    .asv-list-search input:focus { border-color: var(--asv-cyan-dim); }
    /* D12 · SUB-TOPIC | # OF CONCEPTS column header for the left-hand panel */
    .asv-list-header {
      display: flex; align-items: center; justify-content: space-between;
      padding: 10px 14px 6px;
      color: var(--asv-cyan); font-size: 10px; font-weight: 600;
      letter-spacing: 1.8px; text-transform: uppercase;
      border-bottom: 1px solid var(--asv-border-2);
    }
    .asv-list-body { flex: 1; overflow-y: auto; }
    /* D12 · left-panel Sub-Topic row layout: [name] [+N NEW pill] [count]
       flex-row so the NEW pill sits between name and count. */
    .asv-st-row {
      display: flex; align-items: center; gap: 8px;
      padding: 10px 14px; border-bottom: 1px solid rgba(34,211,238,0.06);
      font-size: 12px; cursor: pointer; user-select: none;
    }
    .asv-st-row:hover { background: rgba(34,211,238,0.06); }
    .asv-st-row.active { background: rgba(34,211,238,0.14); border-left: 2px solid var(--asv-cyan); padding-left: 12px; }
    .asv-st-row.amber { border-left: 2px solid var(--asv-amber); padding-left: 12px; }
    .asv-st-name { flex: 1; min-width: 0; color: #e0f7fa; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .asv-st-num  { color: var(--asv-cyan-dim); font-size: 12px; text-align: right; font-variant-numeric: tabular-nums; flex-shrink: 0; min-width: 32px; }
    .asv-new-pill {
      display: inline-block; flex-shrink: 0;
      background: rgba(251,191,36,0.14);
      border: 1px solid rgba(251,191,36,0.55);
      color: #fef3c7;
      font-size: 9px; font-weight: 700; letter-spacing: 1.4px;
      padding: 2px 7px; border-radius: 999px;
      text-transform: uppercase;
    }
    .asv-list-footer { padding: 10px 14px; border-top: 1px solid var(--asv-border-2); font-size: 10px; color: var(--asv-cyan-dim); display: flex; justify-content: space-between; }

    .asv-right-col { position: relative; overflow: hidden; background: var(--asv-panel); border: 1px solid var(--asv-border-2); border-radius: 10px; display: flex; flex-direction: column; }
    .asv-graph-header { padding: 10px 16px; border-bottom: 1px solid var(--asv-border-2); font-size: 11px; color: var(--asv-cyan-dim); flex: 0 0 auto; }
    .asv-g-title { color: #f0fdff; font-size: 12px; }
    .asv-g-title strong { color: var(--asv-cyan); letter-spacing: 0.4px; }
    .asv-graph-stage { flex: 1; position: relative; }
    .asv-graph-svg { width: 100%; height: 100%; display: block; cursor: grab; }
    .asv-graph-svg:active { cursor: grabbing; }
    .asv-graph-legend { position: absolute; bottom: 10px; left: 14px; font-size: 10px; color: var(--asv-cyan-dim); display: flex; gap: 14px; z-index: 3; }
    .asv-graph-legend .dot { width: 8px; height: 8px; border-radius: 50%; display: inline-block; margin-right: 6px; vertical-align: middle; }
    .asv-hovercard { position: absolute; pointer-events: none; background: rgba(6,14,30,0.96); border: 1px solid var(--asv-border); color: var(--asv-cyan-txt); padding: 8px 12px; border-radius: 8px; font-size: 11px; max-width: 280px; z-index: 20; box-shadow: 0 8px 24px rgba(0,0,0,0.5); display: none; }
    .asv-hc-title { color: #f0fdff; font-weight: 600; font-size: 12px; margin-bottom: 4px; }
    .asv-hc-stats { color: var(--asv-cyan-txt); margin-bottom: 3px; line-height: 1.5; }
    .asv-hc-cta { color: var(--asv-cyan); font-size: 10px; letter-spacing: 1.2px; text-transform: uppercase; margin-top: 6px; padding-top: 6px; border-top: 1px solid var(--asv-border-2); }

    .asv-listview { padding: 20px 40px; flex: 1; overflow-y: auto; }
    .asv-listview h3 { margin: 0 0 4px; color: #f0fdff; font-size: 14px; font-weight: 600; }
    .asv-lv-sub { margin: 0 0 22px; color: var(--asv-cyan-dim); font-size: 11px; }
    .asv-lv-table { width: 100%; border-collapse: separate; border-spacing: 0; font-size: 12px; }
    .asv-lv-table thead th { text-align: left; padding: 10px 14px; color: var(--asv-cyan-dim); font-weight: 500; font-size: 10px; letter-spacing: 2px; text-transform: uppercase; border-bottom: 1px solid var(--asv-border); background: rgba(0,0,0,0.2); position: sticky; top: 0; }
    .asv-lv-table thead th.num { text-align: right; }
    .asv-lv-table tbody tr { cursor: pointer; user-select: none; }
    .asv-lv-table tbody tr:hover { background: rgba(34,211,238,0.06); }
    .asv-lv-table tbody td { padding: 10px 14px; border-bottom: 1px solid rgba(34,211,238,0.06); color: #e0f7fa; }
    .asv-lv-table tbody td.num { color: var(--asv-cyan-dim); font-variant-numeric: tabular-nums; text-align: right; }
    .asv-lv-table tbody tr.amber td.name { color: #fef3c7; }
    .asv-lv-table tbody td.drill { text-align: right; color: var(--asv-cyan); font-weight: 600; }
    .asv-amber-pill { display: inline-block; margin-left: 8px; font-size: 9px; padding: 2px 8px; background: rgba(251,191,36,0.15); border: 1px solid var(--asv-amber-dim); border-radius: 999px; color: var(--asv-amber); letter-spacing: 1.5px; text-transform: uppercase; }
    .asv-amber-pill.big { font-size: 10px; padding: 3px 12px; letter-spacing: 2px; font-weight: 600; }

    .asv-wiki { padding: 24px 44px 40px; flex: 1; overflow-y: auto; }
    .asv-wiki-crumb { font-size: 11px; color: var(--asv-cyan-dim); letter-spacing: 1.5px; text-transform: uppercase; margin-bottom: 6px; }
    .asv-wiki-title { color: #f0fdff; font-size: 26px; font-weight: 600; letter-spacing: 0.4px; margin: 0; display: flex; align-items: center; gap: 14px; }
    .asv-wiki-stats { display: flex; gap: 22px; margin: 12px 0 22px; font-size: 12px; color: var(--asv-cyan-dim); flex-wrap: wrap; }
    .asv-wiki-stats .stat { display: flex; align-items: baseline; gap: 6px; }
    .asv-wiki-stats .stat strong { color: #f0fdff; font-weight: 600; font-size: 15px; }
    .asv-wiki-stats .stat.amber strong { color: var(--asv-amber); }
    .asv-wiki-h { font-size: 10px; color: var(--asv-cyan-dim); letter-spacing: 2.5px; text-transform: uppercase; margin: 28px 0 10px; display: flex; align-items: center; gap: 10px; }
    .asv-wiki-h::after { content: ''; flex: 1; height: 1px; background: var(--asv-border-2); }
    .asv-wiki-desc { color: var(--asv-cyan-txt); font-size: 12px; line-height: 1.65; margin: 0 0 4px; max-width: 780px; }
    .asv-newest-card { background: linear-gradient(135deg, rgba(251,191,36,0.10), rgba(251,191,36,0.02)); border: 1px solid var(--asv-amber-dim); border-radius: 10px; padding: 16px 20px; margin-top: 10px; display: grid; grid-template-columns: 1fr auto; gap: 20px; align-items: center; }
    .asv-nc-badge { font-size: 9px; letter-spacing: 2px; text-transform: uppercase; color: var(--asv-amber); font-weight: 700; margin-bottom: 4px; display: flex; align-items: center; gap: 6px; }
    .asv-nc-badge .pulse { width: 6px; height: 6px; border-radius: 50%; background: var(--asv-amber); box-shadow: 0 0 10px var(--asv-amber); }
    .asv-nc-name { color: #fef3c7; font-size: 15px; font-weight: 600; margin: 2px 0 6px; }
    .asv-nc-source { color: var(--asv-amber-dim); font-size: 11px; margin-bottom: 6px; }
    .asv-nc-links { color: var(--asv-cyan-txt); font-size: 11px; }
    .asv-nc-links strong { color: #f0fdff; font-weight: 500; }
    .asv-newest-card button { border: 1px solid var(--asv-amber-dim); background: transparent; color: var(--asv-amber); padding: 8px 14px; border-radius: 8px; cursor: pointer; font-size: 11px; letter-spacing: 1.5px; text-transform: uppercase; font-weight: 600; }
    .asv-newest-card button:hover { background: rgba(251,191,36,0.10); }
    .asv-wiki-table { width: 100%; border-collapse: separate; border-spacing: 0; font-size: 12px; margin-top: 6px; }
    .asv-wiki-table thead th { text-align: left; padding: 10px 14px; color: var(--asv-cyan-dim); font-weight: 500; font-size: 10px; letter-spacing: 2px; text-transform: uppercase; border-bottom: 1px solid var(--asv-border); }
    .asv-wiki-table thead th.num { text-align: right; }
    .asv-wiki-table tbody tr { cursor: pointer; user-select: none; }
    .asv-wiki-table tbody tr:hover { background: rgba(34,211,238,0.06); }
    .asv-wiki-table tbody td { padding: 10px 14px; border-bottom: 1px solid rgba(34,211,238,0.06); color: #e0f7fa; }
    .asv-wiki-table tbody td.num { color: var(--asv-cyan-dim); font-variant-numeric: tabular-nums; text-align: right; }
    .asv-wiki-table tbody tr.amber td.name { color: #fef3c7; }
    .asv-new-tag { display: inline-block; margin-left: 8px; font-size: 8px; padding: 2px 7px; background: rgba(251,191,36,0.15); border: 1px solid var(--asv-amber-dim); border-radius: 999px; color: var(--asv-amber); letter-spacing: 1.5px; text-transform: uppercase; }

    .asv-doc-list { display: flex; flex-direction: column; gap: 8px; margin-top: 6px; }
    .asv-doc-row { display: flex; justify-content: space-between; align-items: center; padding: 10px 14px; border: 1px solid var(--asv-border-2); border-radius: 8px; font-size: 12px; cursor: pointer; }
    .asv-doc-row.amber { border-color: var(--asv-amber-dim); background: rgba(251,191,36,0.06); }
    .asv-doc-row .d-name { color: #e0f7fa; }
    .asv-doc-row.amber .d-name { color: #fef3c7; }
    .asv-doc-row .d-meta { color: var(--asv-cyan-dim); font-size: 10px; }

    .asv-impact { padding: 24px 44px 40px; flex: 1; overflow-y: auto; }
    .asv-impact-hero { background: linear-gradient(135deg, rgba(251,191,36,0.12), rgba(251,191,36,0.02)); border: 1px solid var(--asv-amber-dim); border-radius: 12px; padding: 20px 24px; margin-bottom: 22px; }
    .asv-impact-hero .h-badge { font-size: 10px; letter-spacing: 2.5px; text-transform: uppercase; color: var(--asv-amber); font-weight: 700; margin-bottom: 6px; }
    .asv-impact-hero h2 { margin: 2px 0 6px; color: #fef3c7; font-size: 22px; font-weight: 600; letter-spacing: 0.3px; }
    .asv-impact-hero .h-meta { color: var(--asv-amber-dim); font-size: 12px; display: flex; gap: 18px; flex-wrap: wrap; }
    .asv-impact-hero .dot-sep { color: rgba(251,191,36,0.30); }
    .asv-impact-tiles { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-bottom: 26px; }
    .asv-tile { background: var(--asv-panel); border: 1px solid var(--asv-border-2); border-radius: 10px; padding: 16px 18px; }
    .asv-tile .lbl { color: var(--asv-cyan-dim); font-size: 10px; letter-spacing: 2px; text-transform: uppercase; margin-bottom: 6px; }
    .asv-tile .val { color: var(--asv-amber); font-size: 28px; font-weight: 700; font-variant-numeric: tabular-nums; line-height: 1; }
    .asv-tile .sub { color: var(--asv-cyan-dim); font-size: 10px; margin-top: 6px; }
    .asv-impact-ctas { display: flex; gap: 12px; margin-top: 20px; }
    .asv-impact-ctas button { padding: 10px 18px; border-radius: 8px; font-size: 11px; letter-spacing: 1.5px; text-transform: uppercase; font-weight: 600; cursor: pointer; border: 1px solid var(--asv-amber); background: var(--asv-amber); color: #050b18; }
    .asv-impact-ctas button:hover { background: #fcd34d; }

    /* ==========================================================
       D6-Q3 · Rail "Full list" collapsable toggle (top-10 → all)
       ========================================================== */
    .asv-rail-full-list-toggle {
      padding: 10px 14px; border-top: 1px solid var(--asv-border);
      display: flex; align-items: center; justify-content: space-between; cursor: pointer;
      color: var(--asv-cyan); font-size: 11px; font-weight: 600;
      letter-spacing: 1.5px; text-transform: uppercase;
      background: rgba(34, 211, 238, 0.06); user-select: none;
    }
    .asv-rail-full-list-toggle:hover { background: rgba(34, 211, 238, 0.12); }
    .asv-rail-full-list-toggle .caret { transition: transform 200ms; display: inline-block; }
    .asv-rail-full-list-toggle .hidden-count { position: absolute; opacity: 0; pointer-events: none; }

    /* ==========================================================
       Sub-Topic Wiki v6 (D12 approved layout) — ported 2026-07-11
       Palette + spacing lifted from
       /app/frontend/public/mockups/subtopic_wiki_v6.html
       ========================================================== */
    .stw-root {
      --stw-line:      #22d3ee;
      --stw-line-soft: rgba(34, 211, 238, 0.40);
      --stw-txt:       #22d3ee;
      --stw-txt-strong:#67e8f9;
      --stw-white:     #f0fdff;
      --stw-amber:     #fbbf24;
      --stw-amber-dim: rgba(251, 191, 36, 0.55);
      --stw-amber-bg:  rgba(251, 191, 36, 0.10);
      --stw-border:    rgba(34, 211, 238, 0.35);
      --stw-border-2:  rgba(34, 211, 238, 0.14);
      flex: 1; overflow-y: auto;
      padding: 0 20px 40px;
    }

    /* Breadcrumb row */
    .stw-breadcrumb-row {
      display: flex; align-items: center; justify-content: space-between;
      padding: 8px 0 10px;
      border-bottom: 1px solid var(--stw-border-2);
      gap: 24px;
    }
    .stw-crumb {
      display: flex; align-items: baseline; gap: 10px;
      font-size: 22px; font-weight: 500; letter-spacing: 0.4px;
      flex: 1; min-width: 0;
      /* D6-2 (2026-02-13): prevent mid-word breaks on narrower
         viewports. Operator flag: "The format has skewed on this page.
         It was good the previous deployment." The prior CSS let
         children shrink below their intrinsic width, so "Sub-Topic:"
         was breaking as "Sub-" / "Topic:" and "SearchInc_TheRevolution"
         wrapped mid-token. flex-wrap moves the whole crumb to a second
         line when the row is too narrow (the acceptable "wrap the
         whole label" fallback); white-space nowrap on the children
         keeps each token intact. */
      flex-wrap: wrap;
    }
    .stw-crumb .prefix { color: var(--stw-line); white-space: nowrap; }
    .stw-crumb .sep { color: var(--stw-line); opacity: 0.5; white-space: nowrap; }
    .stw-crumb .subtopic { color: var(--stw-white); text-transform: capitalize; white-space: nowrap; }
    .stw-new-inline {
      color: var(--stw-amber); font-size: 10px; letter-spacing: 1.5px;
      margin-left: 6px; font-weight: 700;
      padding: 3px 8px; border: 1px solid var(--stw-amber-dim);
      background: var(--stw-amber-bg); border-radius: 999px;
    }

    /* View-mode two-stack (D9-4 / D10) */
    .stw-view-mode-stack {
      display: flex; flex-direction: column; gap: 6px;
      min-width: 240px; flex-shrink: 0;
    }
    .stw-view-mode-stack .vm-row {
      padding: 8px 16px;
      font-size: 11px; font-weight: 600;
      letter-spacing: 1.6px; text-transform: uppercase;
      color: #a5f3fc;
      background: rgba(34, 211, 238, 0.14);
      border: 1px solid rgba(34, 211, 238, 0.35);
      border-radius: 4px;
      cursor: pointer; user-select: none;
      text-align: center;
      transition: background 120ms, color 120ms, border-color 120ms;
    }
    .stw-view-mode-stack .vm-row:hover {
      background: rgba(34, 211, 238, 0.22);
      border-color: rgba(34, 211, 238, 0.55);
      color: var(--stw-line);
    }

    /* D6-10 stat strip: two parallel iceberg lines top+bottom, 5 cells */
    .stw-stat-strip {
      display: grid; grid-template-columns: repeat(5, 1fr);
      padding: 13px 8px;
      border-top: 2px solid var(--stw-line);
      border-bottom: 2px solid var(--stw-line);
      margin: 14px 0 0;
    }
    .stw-stat-cell {
      display: flex; flex-direction: column; align-items: center; justify-content: center;
      text-align: center; padding: 0 12px;
    }
    .stw-stat-cell .sc-lbl { font-size: 11px; font-weight: 600; letter-spacing: 0.2px; color: var(--stw-line); line-height: 1.2; margin-bottom: 6px; }
    .stw-stat-cell .sc-val { font-size: 21px; font-weight: 400; font-variant-numeric: tabular-nums; color: var(--stw-white); line-height: 1; margin-bottom: 5px; }
    .stw-stat-cell .sc-sub { font-size: 9px; color: var(--stw-line); }
    .stw-stat-cell.amber .sc-lbl { color: var(--stw-amber); }
    .stw-stat-cell.amber .sc-val { color: #fef3c7; }
    .stw-stat-cell.amber .sc-sub { color: var(--stw-amber); }

    /* Table */
    .stw-table-section { padding: 8px 0 40px; }
    .stw-table-toolbar { display: flex; align-items: center; justify-content: space-between; margin: 16px 0 8px; }
    .stw-table-toolbar .tt-actions { display: flex; align-items: center; gap: 8px; }
    .stw-table-toolbar .tt-filter { background: rgba(0,0,0,0.35); border: 1px solid var(--stw-border); color: var(--stw-txt); padding: 6px 10px; border-radius: 6px; font-size: 12px; outline: none; width: 220px; }
    .stw-table-toolbar .tt-filter::placeholder { color: var(--stw-line); }
    .stw-table-toolbar .tt-filter:focus { border-color: var(--stw-line); }
    .stw-table-toolbar .tt-toggle {
      background: rgba(0,0,0,0.35); border: 1px solid var(--stw-border);
      color: var(--stw-line); padding: 6px 12px; border-radius: 6px;
      font-size: 11px; cursor: pointer; letter-spacing: 1px; text-transform: uppercase;
    }
    .stw-table-toolbar .tt-toggle:hover { border-color: var(--stw-line); }
    .stw-table-toolbar .tt-toggle.on {
      background: var(--stw-amber-bg); border-color: var(--stw-amber-dim); color: var(--stw-amber);
    }

    .stw-ct { width: 100%; border-collapse: separate; border-spacing: 0; font-size: 13px; }
    .stw-ct thead th {
      text-align: left; padding: 12px 14px; color: var(--stw-line); font-weight: 700;
      font-size: 11px; letter-spacing: 1.8px; text-transform: uppercase;
      border-bottom: 1px solid var(--stw-line); background: rgba(0,0,0,0.28);
      cursor: pointer; user-select: none;
    }
    .stw-ct thead th:hover { color: var(--stw-txt-strong); }
    .stw-ct thead th.num { text-align: right; }
    .stw-ct thead th .sort-caret { display: inline-block; margin-left: 4px; opacity: 0.4; font-size: 9px; }
    .stw-ct thead th.sort-active { color: var(--stw-txt-strong); }
    .stw-ct thead th.sort-active .sort-caret { opacity: 1; }

    .stw-ct tbody tr.crow { cursor: pointer; transition: background 100ms; }
    .stw-ct tbody tr.crow:hover { background: rgba(34, 211, 238, 0.06); }
    .stw-ct tbody tr.crow.amber { background: rgba(251, 191, 36, 0.06); }
    .stw-ct tbody tr.crow.amber:hover { background: rgba(251, 191, 36, 0.12); }
    .stw-ct tbody tr.crow.expanded { background: rgba(34, 211, 238, 0.10); }
    .stw-ct tbody tr.crow.amber.expanded { background: rgba(251, 191, 36, 0.13); }
    .stw-ct tbody td { padding: 12px 14px; border-bottom: 1px solid rgba(34, 211, 238, 0.08); color: var(--stw-white); vertical-align: top; }
    .stw-ct tbody td.num { color: var(--stw-line); font-variant-numeric: tabular-nums; text-align: right; }
    .stw-ct .cn { color: var(--stw-white); font-weight: 500; }
    .stw-ct .cn-def { color: var(--stw-line); font-size: 12px; margin-top: 4px; line-height: 1.5;
      display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical; overflow: hidden; }
    .stw-ct .new-tag {
      display: inline-block; margin-left: 8px; font-size: 9px; padding: 2px 8px;
      background: var(--stw-amber-bg); border: 1px solid var(--stw-amber-dim);
      border-radius: 999px; color: var(--stw-amber);
      letter-spacing: 1.5px; text-transform: uppercase; font-weight: 700; vertical-align: 2px;
    }
    .stw-ct .caret { color: var(--stw-line); font-size: 10px; margin-right: 6px; display: inline-block; width: 10px; transition: transform 160ms; }
    .stw-ct tbody tr.expanded .caret { transform: rotate(90deg); color: var(--stw-txt-strong); }

    .stw-ct .qbar { display: inline-flex; align-items: center; gap: 6px; }
    .stw-ct .qb-track { width: 60px; height: 5px; border-radius: 3px; background: rgba(34, 211, 238, 0.14); overflow: hidden; }
    .stw-ct .qb-fill { height: 100%; background: linear-gradient(90deg, var(--stw-line), var(--stw-txt-strong)); border-radius: 3px; }
    .stw-ct .qb-num { color: var(--stw-line); font-size: 11px; font-variant-numeric: tabular-nums; min-width: 26px; text-align: right; }

    .stw-ct .stw-empty { text-align: center; padding: 40px; color: var(--stw-line); font-size: 12px; }

    /* Row-expansion detail panel */
    .stw-ct tr.stw-detail-row td { padding: 14px 14px 20px 44px; background: rgba(6, 14, 30, 0.55); border-bottom: 1px solid var(--stw-border-2); }
    .stw-ct .detail-grid { display: grid; grid-template-columns: 1.4fr 1fr; gap: 24px; }
    .stw-ct .detail-h { color: var(--stw-line); font-size: 11px; font-weight: 700; letter-spacing: 1.6px; text-transform: uppercase; margin: 0 0 8px; }
    .stw-ct .detail-summary { color: var(--stw-txt); font-size: 13px; line-height: 1.65; white-space: pre-wrap; }
    .stw-ct .detail-summary strong { color: var(--stw-white); font-weight: 600; }
    .stw-ct .detail-claims { list-style: none; padding: 0; margin: 0; }
    .stw-ct .detail-claims li {
      color: var(--stw-txt); font-size: 12px; padding: 8px 10px;
      border-left: 2px solid var(--stw-line-soft); background: rgba(34, 211, 238, 0.04);
      border-radius: 0 6px 6px 0; margin-bottom: 6px; line-height: 1.5;
    }
    .stw-ct .detail-conns { list-style: none; padding: 0; margin: 8px 0 0; }
    .stw-ct .detail-conns li {
      display: flex; justify-content: space-between; align-items: center;
      padding: 7px 10px; border: 1px solid var(--stw-border-2); border-radius: 6px;
      margin-bottom: 6px; font-size: 12px; color: var(--stw-white); cursor: pointer;
    }
    .stw-ct .detail-conns li:hover { border-color: var(--stw-line); background: rgba(34, 211, 238, 0.06); }
    .stw-ct .detail-conns .cn-tgt { display: flex; align-items: center; gap: 8px; }
    .stw-ct .detail-conns .cn-type {
      font-size: 9px; padding: 2px 6px; background: rgba(34, 211, 238, 0.10);
      border-radius: 999px; color: var(--stw-line); letter-spacing: 1.2px; text-transform: uppercase;
    }
    .stw-ct .detail-conns .cn-w {
      font-size: 11px; font-variant-numeric: tabular-nums; color: var(--stw-line);
      padding: 1px 6px; border: 1px solid var(--stw-border-2); border-radius: 4px;
    }
    .stw-ct .detail-empty { color: var(--stw-line); font-size: 11px; padding: 8px 0; opacity: 0.7; }
    .stw-ct .detail-sources { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
    .stw-ct .detail-sources .src-pill {
      display: inline-flex; align-items: center; gap: 6px; padding: 5px 10px;
      border: 1px solid var(--stw-border-2); background: rgba(0,0,0,0.30);
      border-radius: 999px; font-size: 11px; color: var(--stw-txt); cursor: pointer;
    }
    .stw-ct .detail-sources .src-pill:hover { border-color: var(--stw-line); }
    .stw-ct .detail-sources .src-pill .dl { color: var(--stw-line); font-size: 10px; }
    .stw-ct .detail-open-cta {
      margin-top: 14px; background: transparent; border: 1px solid var(--stw-border);
      color: var(--stw-line); padding: 8px 14px; border-radius: 8px; cursor: pointer;
      font-size: 11px; letter-spacing: 1.2px; text-transform: uppercase; font-weight: 600;
    }
    .stw-ct .detail-open-cta:hover { background: rgba(34, 211, 238, 0.10); border-color: var(--stw-line); }

    /* D7-5 "Show all N Concepts" collapsable */
    .stw-table-show-all {
      display: flex; align-items: center; justify-content: center; gap: 10px;
      padding: 12px 16px; margin-top: 8px;
      border: 1px solid var(--stw-border); border-radius: 8px;
      color: var(--stw-line); background: rgba(34, 211, 238, 0.06);
      cursor: pointer; user-select: none;
      font-size: 12px; font-weight: 600; letter-spacing: 1.2px; text-transform: uppercase;
    }
    .stw-table-show-all:hover { background: rgba(34, 211, 238, 0.12); }
    .stw-table-show-all .tsa-caret { font-size: 10px; }

    /* Global definition tooltip */
    [data-tip] { cursor: help; }
    .asv-def-tip {
      position: fixed; z-index: 9999; pointer-events: none;
      background: rgba(6,14,30,0.98); border: 1px solid var(--asv-cyan-dim, rgba(34,211,238,0.60));
      color: var(--asv-cyan-txt, #a5f3fc); padding: 9px 12px; border-radius: 8px;
      font-size: 11px; line-height: 1.55; max-width: 320px;
      box-shadow: 0 12px 30px rgba(0,0,0,0.7);
      display: none;
    }
    .asv-dt-label { color: #22d3ee; font-size: 10px; text-transform: uppercase; letter-spacing: 1.5px; margin-bottom: 4px; }
  `}</style>
);
